/** Rolling input/render metrics for the debug panel. */
export interface StatsSnapshot {
  sampleHz: number;
  coalescedPerFrame: { avg: number; max: number };
  predictedPerFrame: { avg: number; max: number };
  liveDrawMs: { avg: number; max: number };
  /** Event timeStamp → live draw finished, ms (JS-visible input latency, excludes display). */
  inputToDrawMs: { avg: number; max: number };
  rasterMs: number;
  compositeFps: number;
  hoverEvents: number;
  hoverSegments: number;
  lastHoverAgoMs: number | null;
  pointer: { type: string; pressure: number; tiltX: number; tiltY: number; button: number; buttons: number; width: number; height: number } | null;
  buttonLog: string[];
  desynchronized: boolean;
  strokesOnPage: number;
  tiles: number;
  mode: string;
}

interface Frame {
  coalesced: number;
  predicted: number;
  drawMs: number;
  latency: number;
}

const WINDOW_MS = 1000;

export class Stats {
  private frames: { t: number; f: Frame }[] = [];
  private cur: Frame | null = null;
  private composites: number[] = [];
  private strokeT: { first: number; last: number; n: number } | null = null;
  private lastStrokeHz = 0;
  private lastHover: number | null = null;
  pointer: StatsSnapshot['pointer'] = null;
  buttonLog: string[] = [];
  rasterMs = 0;
  hoverEvents = 0;
  hoverSegments = 0;
  desynchronized = false;
  strokesOnPage = 0;
  tiles = 0;
  mode = 'pen';

  /** One pen move event's worth of work. Aggregated into the current animation frame. */
  onMove(coalesced: number, predicted: number, drawMs: number, latency: number): void {
    const f = (this.cur ??= { coalesced: 0, predicted: 0, drawMs: 0, latency: 0 });
    f.coalesced += coalesced;
    f.predicted = Math.max(f.predicted, predicted);
    f.drawMs += drawMs;
    f.latency = Math.max(f.latency, latency);
  }

  onSamples(timeStamps: number[]): void {
    for (const t of timeStamps) {
      if (!this.strokeT) this.strokeT = { first: t, last: t, n: 1 };
      else { this.strokeT.last = t; this.strokeT.n++; }
    }
  }

  onStrokeEnd(): void {
    const s = this.strokeT;
    if (s && s.n > 5 && s.last > s.first) this.lastStrokeHz = ((s.n - 1) * 1000) / (s.last - s.first);
    this.strokeT = null;
  }

  onHover(): void {
    this.hoverEvents++;
    this.lastHover = performance.now();
  }

  onComposite(): void {
    this.composites.push(performance.now());
  }

  logButtons(type: string, button: number, buttons: number, pointerType: string): void {
    const line = `${new Date().toLocaleTimeString()} ${type} ${pointerType} button=${button} buttons=${buttons}`;
    if (this.buttonLog[0]?.endsWith(line.slice(line.indexOf(' ')))) return;
    this.buttonLog.unshift(line);
    this.buttonLog.length = Math.min(this.buttonLog.length, 10);
  }

  /** Call once per animation frame. */
  tick(now = performance.now()): void {
    if (this.cur) {
      this.frames.push({ t: now, f: this.cur });
      this.cur = null;
    }
    while (this.frames.length && now - this.frames[0]!.t > WINDOW_MS) this.frames.shift();
    while (this.composites.length && now - this.composites[0]! > WINDOW_MS) this.composites.shift();
  }

  snapshot(): StatsSnapshot {
    const agg = (pick: (f: Frame) => number) => {
      if (this.frames.length === 0) return { avg: 0, max: 0 };
      let sum = 0, max = 0;
      for (const { f } of this.frames) { const v = pick(f); sum += v; if (v > max) max = v; }
      return { avg: sum / this.frames.length, max };
    };
    const s = this.strokeT;
    const liveHz = s && s.n > 5 && s.last > s.first ? ((s.n - 1) * 1000) / (s.last - s.first) : 0;
    return {
      sampleHz: liveHz || this.lastStrokeHz,
      coalescedPerFrame: agg((f) => f.coalesced),
      predictedPerFrame: agg((f) => f.predicted),
      liveDrawMs: agg((f) => f.drawMs),
      inputToDrawMs: agg((f) => f.latency),
      rasterMs: this.rasterMs,
      compositeFps: this.composites.length,
      hoverEvents: this.hoverEvents,
      hoverSegments: this.hoverSegments,
      lastHoverAgoMs: this.lastHover === null ? null : performance.now() - this.lastHover,
      pointer: this.pointer,
      buttonLog: this.buttonLog,
      desynchronized: this.desynchronized,
      strokesOnPage: this.strokesOnPage,
      tiles: this.tiles,
      mode: this.mode,
    };
  }
}
